// hangar.link / fleetviewer.link canvas export-import compatibility.
//
// The canvas viewer at https://hangar.link/fleet/canvas uses the
// `starjumpFleetviewer` JSON format (v1) — a positional layout of ships
// referenced by `shipSlug` strings that resolve against fleetviewer.link's
// internal SC ship database. The format is NOT the same as the older
// `hangarexport` pledge format (that's for the CCU/buyback tracker).
//
// Sample structure (abridged):
//
//   {
//     "type": "starjumpFleetviewer", "version": 1,
//     "backgroundColor": "00151515",
//     "backgroundImageUrl": "https://cdn3.fleetviewer.link/starjump-spacescape.png",
//     "canvasZoom": "0.0372759372", "canvasPanX": "0", "canvasPanY": "0",
//     // … viewport + render box config …
//     "canvasItems": [
//       { "id": "<uuid>", "itemType": "SHIP",      "shipSlug": "100i", "x": "0", "y": "0", "width": "3800", "height": "2512", … },
//       { "id": "<uuid>", "itemType": "TEXTGROUP", "text": "100i", "childOfShip": "<ship-uuid>", … }
//     ]
//   }
//
// Each ship is paired with a TEXTGROUP label below it (childOfShip / parentOfGroup).

import { getShipStatus, SHIP_STATUS } from './FleetFilterBar';

// ─── Slug + name helpers ─────────────────────────────────────────────────────

function shipToSlug(name) {
  // fleetviewer slugs are lowercase model names with non-alphanumerics
  // collapsed to hyphens. "Ares Ion" → "ares-ion", "600i Executive" → "600i-executive".
  // We don't have a canonical slug column in our ships table, so this is a
  // best-effort derivation. Ships whose slug doesn't match fleetviewer's
  // database will silently fail to render on the canvas — but the file
  // structure is still valid and the user can edit slugs on the canvas.
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normaliseShipName(name) {
  return String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
}

function buildShipNameIndex(scShips) {
  const idx = new Map();
  for (const s of scShips || []) {
    const n = normaliseShipName(s.name);
    if (n) idx.set(n, s);
    if (s.manufacturer) {
      const withMfr = normaliseShipName(`${s.manufacturer} ${s.name}`);
      if (withMfr && !idx.has(withMfr)) idx.set(withMfr, s);
    }
  }
  return idx;
}

// Estimate a ship's render dimensions on the canvas based on category. The
// real fleetviewer DB has exact ship lengths; we don't, so categories give a
// good-enough scale so big ships occupy more space than fighters.
function estimateShipSize(ship) {
  const cat = String(ship?.category || "").toLowerCase();
  if (cat.includes("capital") || cat.includes("carrier")) return { w: 20000, h: 12000 };
  if (cat.includes("cargo") || cat.includes("merchant") || cat.includes("expedition")) return { w: 9000, h: 5500 };
  if (cat.includes("explorer") || cat.includes("transport") || cat.includes("touring")) return { w: 7000, h: 4500 };
  if (cat.includes("medical") || cat.includes("salvage") || cat.includes("mining")) return { w: 6500, h: 4000 };
  if (cat.includes("fighter") || cat.includes("interceptor")) return { w: 3500, h: 2400 };
  if (cat.includes("ground") || cat.includes("vehicle") || cat.includes("rover")) return { w: 2500, h: 1500 };
  return { w: 5000, h: 3200 }; // sensible default for a midsize ship
}

function uuid() {
  // RFC4122 v4-ish; canvas IDs don't have to be cryptographically secure.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── EXPORT — our ships → starjumpFleetviewer canvas JSON ────────────────────

/**
 * Build a hangar.link canvas-viewer file from our profile ship entries.
 *
 * @param {Array}  entries     — selected entries from profiles.ships ({ship_id, quantity, primary, status})
 * @param {Map}    shipsById   — Map of ship_id → ship row from public.ships
 * @param {string} handle      — used only as a marker in the metadata
 * @returns {Object}           — the JSON object ready to JSON.stringify
 */
export function buildHangarLinkExport(entries, shipsById, handle) {
  const canvasItems = [];
  // Grid layout — fit ~5 ships per row, large gutter so capital ships have room.
  const COLS = 5;
  const CELL_W = 25000;
  const CELL_H = 18000;
  const LABEL_GAP = 1800; // px below the ship for its TEXTGROUP label

  let idx = 0;
  for (const entry of entries || []) {
    const ship = shipsById?.get(entry.ship_id);
    if (!ship) continue;
    const qty = Math.max(1, Number(entry.quantity) || 1);

    // Each hull is a separate SHIP item on the canvas (matches how fleetviewer
    // treats multiples — quantity ×3 becomes three positioned ships).
    for (let i = 0; i < qty; i++) {
      const col = idx % COLS;
      const row = Math.floor(idx / COLS);
      const x = (col - (COLS - 1) / 2) * CELL_W;
      const y = (row - 1) * CELL_H;
      const size = estimateShipSize(ship);
      const shipId = uuid();
      const labelId = uuid();
      const slug = shipToSlug(ship.name);
      const displayName = ship.name;

      canvasItems.push({
        id: shipId,
        itemType: "SHIP",
        x: x.toFixed(10),
        y: y.toFixed(10),
        topRotation: "0.0000000000",
        rotation: "0.0000000000",
        zoom: "1.0000000000",
        viewAngle: "iso",
        imageRes: size.w >= 10000 ? "l" : "s",
        imageScale: (size.w / 1000).toFixed(10),
        dropShadow: "true",
        elevation: "15.0000000000",
        shipSlug: slug,
        variantSlug: "",
        width: size.w.toFixed(10),
        height: size.h.toFixed(10),
        autoPositionDistance: "800.0000000000",
        autoPositionAlignment: "auto",
        defaultText: displayName,
        locked: "false",
        selectionGroup: "",
        parentOfGroup: labelId,
      });
      canvasItems.push({
        id: labelId,
        itemType: "TEXTGROUP",
        x: x.toFixed(10),
        y: (y + size.h / 2 + LABEL_GAP).toFixed(10),
        rotation: "0.0000000000",
        text: displayName,
        width: Math.max(1500, displayName.length * 200).toFixed(10),
        height: "1200.0000000000",
        align: "left",
        fontRes: "m",
        fontFamily: "Font1",
        statfontRes: "s",
        statfontFamily: "Font1",
        childOfShip: shipId,
        stats: ["LENGTH"],
        locked: "false",
        selectionGroup: "",
        isometricMode: "none",
        gradientType: "none",
        gradientColor1: "b3ffffff",
        gradientColor2: "ff000000",
        gradientColor3: "ff808080",
        gradientAngle: "90.0000000000",
        gradientCenterX: "0.5000000000",
        gradientCenterY: "0.5000000000",
        gradientRadius: "0.8000000000",
        sweepStartAngle: "0.0000000000",
        sweepStopAngle: "360.0000000000",
        sweepAngleOffset: "0.0000000000",
        dropShadow: "false",
        elevation: "0.0000000000",
        glow: "false",
        glowColor: "ffffffff",
        glowRadius: "0.0000000000",
      });
      idx += 1;
    }
  }

  return {
    type: "starjumpFleetviewer",
    version: 1,
    backgroundColor: "00151515",
    backgroundImageUrl: "https://cdn3.fleetviewer.link/starjump-spacescape.png",
    backgroundImageBase64: "",
    backgroundRotation: "0.0000000000",
    // A modest zoom so all ships are visible on first load. Users can pan/zoom.
    canvasZoom: "0.0200000000",
    canvasPanX: "0.0000000000",
    canvasPanY: "0.0000000000",
    globalItemZoom: "0.0200000000",
    globalItemPanX: "0.0000000000",
    globalItemPanY: "0.0000000000",
    viewportZoom: "0.8937500000",
    viewportPanX: "-0.0000000000",
    viewportPanY: "-0.0000000000",
    viewportMinZoom: "0.0500000000",
    viewportMaxZoom: "8.0000000000",
    renderBoxX: "0.0000000000",
    renderBoxY: "0.0000000000",
    renderBoxWidth: "3840.0000000000",
    renderBoxHeight: "2160.0000000000",
    communityIconX: "1820.0000000000",
    communityIconY: "980.0000000000",
    starjumpIconX: "-1795.0000000000",
    starjumpIconY: "955.0000000000",
    shipScaleFactor: "4.0000000000",
    minZoom: "0.0010000000",
    maxZoom: "1.0000000000",
    snapEnabled: "false",
    snapSize: "1000.0000000000",
    snapMode: "center",
    defaultElevation: "15.0000000000",
    canvasItems,
    // Non-standard markers — hangar.link ignores unknown top-level keys.
    starkeeper_export: {
      source: "Starkeeper Industries",
      handle: handle || null,
      exported_at: new Date().toISOString(),
      // Preserve our pledged/in-game status keyed by ship-uuid so we can re-import.
      statuses: (entries || []).reduce((acc, e) => {
        acc[e.ship_id] = getShipStatus(e);
        return acc;
      }, {}),
    },
  };
}

// ─── IMPORT — accept either starjumpFleetviewer or hangarexport ──────────────

/**
 * Parse a fleet file. Accepts both starjumpFleetviewer (canvas) and the older
 * hangarexport (pledge tracker) formats. Returns a normalised result.
 *
 * @param {*}     raw     — parsed JSON object
 * @param {Array} scShips — public.ships rows
 * @returns { matched, unmatched, totals, handle, source, error? }
 */
export function parseHangarLinkImport(raw, scShips) {
  if (!raw || typeof raw !== "object") {
    return { matched: [], unmatched: [], totals: { pledges: 0, items: 0 }, handle: null, source: null, error: "Not a JSON object" };
  }
  const idx = buildShipNameIndex(scShips);

  // ── starjumpFleetviewer (canvas) ──────────────────────────────────────────
  if (raw.type === "starjumpFleetviewer") {
    const items = Array.isArray(raw.canvasItems) ? raw.canvasItems : [];
    const ships = items.filter(it => it?.itemType === "SHIP");
    const byShipId = new Map();
    const unmatchedByTitle = new Map();
    const statuses = raw?.starkeeper_export?.statuses || {};
    for (const s of ships) {
      // Try defaultText first (the human-readable label) then variantSlug/shipSlug forms.
      const candidates = [s.defaultText, s.shipSlug && s.shipSlug.replace(/-/g, " "), s.variantSlug && s.variantSlug.replace(/-/g, " ")]
        .filter(Boolean)
        .map(String);
      let matched = null;
      for (const c of candidates) {
        const hit = idx.get(normaliseShipName(c));
        if (hit) { matched = hit; break; }
      }
      if (matched) {
        const ex = byShipId.get(matched.id);
        const status = statuses[matched.id] === SHIP_STATUS.IN_GAME ? SHIP_STATUS.IN_GAME : SHIP_STATUS.PLEDGED;
        if (ex) { ex.quantity += 1; }
        else { byShipId.set(matched.id, { ship_id: matched.id, name: matched.name, quantity: 1, status, ship: matched }); }
      } else {
        const title = s.defaultText || s.shipSlug || "(unknown)";
        const key = normaliseShipName(title);
        const ex = unmatchedByTitle.get(key);
        if (ex) ex.quantity += 1;
        else unmatchedByTitle.set(key, { title, quantity: 1, kinds: ["Spaceship"] });
      }
    }
    return {
      matched: [...byShipId.values()].sort((a, b) => a.name.localeCompare(b.name)),
      unmatched: [...unmatchedByTitle.values()].sort((a, b) => a.title.localeCompare(b.title)),
      totals: { pledges: 0, items: ships.length },
      handle: raw?.starkeeper_export?.handle || null,
      source: raw?.starkeeper_export?.source || "hangar.link canvas",
    };
  }

  // ── hangarexport (pledge tracker, from the SC-Open RSI extension) ─────────
  if (raw.type === "hangarexport") {
    const pledges = Array.isArray(raw.pledges) ? raw.pledges : [];
    const byShipId = new Map();
    const unmatchedByTitle = new Map();
    let totalItems = 0;
    for (const p of pledges) {
      const pStatus = p?.starkeeper_status === SHIP_STATUS.IN_GAME ? SHIP_STATUS.IN_GAME : SHIP_STATUS.PLEDGED;
      const items = Array.isArray(p?.items) ? p.items : [];
      for (const it of items) {
        const kind = String(it?.kind || "").toLowerCase();
        const looksLikeShip = !kind || kind.includes("ship") || kind.includes("vehicle");
        if (!looksLikeShip) continue;
        const title = String(it?.title || "").trim();
        if (!title) continue;
        totalItems += 1;
        const ship = idx.get(normaliseShipName(title));
        if (ship) {
          const existing = byShipId.get(ship.id);
          if (existing) { existing.quantity += 1; }
          else { byShipId.set(ship.id, { ship_id: ship.id, name: ship.name, quantity: 1, status: pStatus, ship }); }
        } else {
          const key = normaliseShipName(title);
          const ex = unmatchedByTitle.get(key);
          if (ex) { ex.quantity += 1; if (it.kind && !ex.kinds.includes(it.kind)) ex.kinds.push(it.kind); }
          else unmatchedByTitle.set(key, { title, quantity: 1, kinds: it.kind ? [it.kind] : [] });
        }
      }
    }
    return {
      matched: [...byShipId.values()].sort((a, b) => a.name.localeCompare(b.name)),
      unmatched: [...unmatchedByTitle.values()].sort((a, b) => a.title.localeCompare(b.title)),
      totals: { pledges: pledges.length, items: totalItems },
      handle: raw.handle || null,
      source: "hangarexport (RSI pledge tracker)",
    };
  }

  return { matched: [], unmatched: [], totals: { pledges: 0, items: 0 }, handle: null, source: null, error: `Unsupported file type: ${raw.type || "(missing)"} — expected "starjumpFleetviewer" or "hangarexport"` };
}

// ─── Mode deltas — what changes if we MERGE vs REPLACE ───────────────────────

export function computeMergeDelta(currentEntries, matchedEntries) {
  const currentById = new Map((currentEntries || []).map(e => [e.ship_id, e]));
  const added = [], increased = [];
  for (const m of matchedEntries || []) {
    const cur = currentById.get(m.ship_id);
    if (!cur) added.push({ ship_id: m.ship_id, name: m.name, quantity: m.quantity });
    else if ((cur.quantity || 1) !== ((cur.quantity || 1) + m.quantity)) {
      increased.push({ ship_id: m.ship_id, name: m.name, from: cur.quantity || 1, to: (cur.quantity || 1) + m.quantity });
    }
  }
  return { added, increased, unchanged: (currentEntries || []).length };
}

export function computeReplaceDelta(currentEntries, matchedEntries) {
  const matchedById = new Map((matchedEntries || []).map(m => [m.ship_id, m]));
  const currentById = new Map((currentEntries || []).map(e => [e.ship_id, e]));
  const removed = [], added = [], changed = [], kept = [];
  for (const cur of currentEntries || []) {
    if (!matchedById.has(cur.ship_id)) removed.push({ ship_id: cur.ship_id, quantity: cur.quantity || 1 });
  }
  for (const m of matchedEntries || []) {
    const cur = currentById.get(m.ship_id);
    if (!cur) added.push({ ship_id: m.ship_id, name: m.name, quantity: m.quantity });
    else if ((cur.quantity || 1) !== m.quantity) changed.push({ ship_id: m.ship_id, name: m.name, from: cur.quantity || 1, to: m.quantity });
    else kept.push({ ship_id: m.ship_id, name: m.name, quantity: m.quantity });
  }
  return { removed, added, changed, kept };
}

export function applyMerge(currentEntries, matchedEntries) {
  const byId = new Map((currentEntries || []).map(e => [e.ship_id, { ...e }]));
  for (const m of matchedEntries || []) {
    const cur = byId.get(m.ship_id);
    if (cur) cur.quantity = (cur.quantity || 1) + m.quantity;
    else byId.set(m.ship_id, { ship_id: m.ship_id, quantity: m.quantity, status: m.status || SHIP_STATUS.PLEDGED });
  }
  return [...byId.values()];
}

export function applyReplace(matchedEntries) {
  return (matchedEntries || []).map(m => ({
    ship_id: m.ship_id,
    quantity: m.quantity,
    status: m.status || SHIP_STATUS.PLEDGED,
  }));
}

// ─── Download helper ─────────────────────────────────────────────────────────

export function downloadJsonFile(data, filename) {
  const text = JSON.stringify(data, null, 2);
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
